import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Col, Card, Badge, Spinner, Alert, Modal, Table } from "react-bootstrap";
import WeekSelector from "../weekSelector";
import { useTranslation } from "react-i18next";
import { SiUber } from "react-icons/si";
import { FaBolt, FaCar, FaReceipt, FaCalculator, FaHandshake, FaUserTie, FaListUl, FaMoneyBillWave, FaUndo, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { AiOutlineBarChart, AiOutlineArrowDown } from "react-icons/ai";

const SERVICE_CONFIG = [
  { id: "uber", label: "Uber", Icon: SiUber, color: "#000000", bg: "dark" },
  { id: "bolt", label: "Bolt", Icon: FaBolt, color: "#00D66C", bg: "success" },
  { id: "freenow", label: "FreeNow", Icon: FaCar, color: "#E30613", bg: "danger" },
];

const formatMoney = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Відсоток зміни відносно середнього за 3 попередні тижні; повертає { percent, isUp } або null
const getChange = (current, avg) => {
  const cur = Number(current) || 0;
  const a = Number(avg) || 0;
  if (a === 0) return null;
  const percent = Math.round(((cur - a) / a) * 1000) / 10;
  if (percent === 0) return null;
  return { percent, isUp: percent > 0 };
};

const CATEGORY_KEYS = [
  { key: "returnVat", labelKey: "returnVAT", hasDetails: false, Icon: FaReceipt },
  { key: "taxesZUS", labelKey: "taxesZUS", hasDetails: false, Icon: FaCalculator },
  { key: "partner", labelKey: "partner", hasDetails: false, Icon: FaHandshake },
  { key: "accountant", labelKey: "accountant", hasDetails: false, Icon: FaUserTie },
  { key: "carRent", labelKey: "carRent", hasDetails: false, Icon: FaCar },
  { key: "otherExpensesSum", labelKey: "otherExpenses", hasDetails: true, detailsField: "other_expenses", Icon: FaListUl },
  { key: "sallary", labelKey: "sallary", hasDetails: false, Icon: FaMoneyBillWave },
  { key: "refunds", labelKey: "refunds", hasDetails: true, detailsField: "refundsDesc", Icon: FaUndo },
];

const WeekStatistics = () => {
  const { t } = useTranslation();
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalCategory, setModalCategory] = useState(null);

  useEffect(() => {
    if (!selectedWeek) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    axios
      .get("/api/week-statistics", {
        params: { week: selectedWeek },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || t("errorLoadingData"));
      })
      .finally(() => setLoading(false));
  }, [selectedWeek, t]);

  return (
    <Container className="py-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <h1 className="h3 mb-0 fw-bold text-primary">
          <AiOutlineBarChart className="me-2" />
          {t("weekStatisticsTitle")}
        </h1>
        <div className="d-flex align-items-center gap-2 flex-grow-1 flex-md-grow-0" style={{ maxWidth: 340 }}>
          <WeekSelector
            selectedWeek={selectedWeek}
            setSelectedWeek={setSelectedWeek}
            availableWeeksOnly
            className="flex-grow-1"
          />
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">{t("loading")}</p>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Доходи брутто по сервісах */}
          <Card className="shadow-sm border-0 mb-4 overflow-hidden">
            <Card.Body className="p-0">
              <Row className="g-0">
                {SERVICE_CONFIG.map(({ id, label, Icon, color, bg }) => (
                  <Col key={id} xs={12} md={4}>
                    <div
                      className="p-4 text-center border-end border-md-bottom-0"
                      style={{
                        borderColor: "var(--bs-border-color)",
                        backgroundColor: id === "bolt" ? "rgba(0,214,108,0.06)" : id === "freenow" ? "rgba(227,6,19,0.06)" : "rgba(0,0,0,0.03)",
                      }}
                    >
                      <Icon size={28} color={color} className="mb-2" />
                      <div className="small text-muted text-uppercase fw-semibold">{label}</div>
                      <div className="h4 mb-0 fw-bold" style={{ color }}>
                        {formatMoney(data.incomeByService[id])} zł
                      </div>
                      {(() => {
                        const ch = getChange(data.incomeByService[id], data.incomeByServicePrev3Avg?.[id]);
                        if (!ch) return null;
                        return (
                          <div className={`d-flex align-items-center justify-content-center gap-1 small mt-1 ${ch.isUp ? "text-success" : "text-danger"}`}>
                            {ch.isUp ? <FaArrowUp size={12} /> : <FaArrowDown size={12} />}
                            <span>{ch.percent > 0 ? "+" : ""}{ch.percent}%</span>
                          </div>
                        );
                      })()}
                    </div>
                  </Col>
                ))}
              </Row>
              <div className="p-3 bg-light border-top">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1 text-start">
                    <span className="fw-semibold text-muted">{t("weekStatisticsTotalGross")}</span>
                  </div>
                  <div className="flex-grow-1 text-center">
                    {(() => {
                      const prev3Avg = (Number(data.incomeByServicePrev3Avg?.uber) || 0) + (Number(data.incomeByServicePrev3Avg?.bolt) || 0) + (Number(data.incomeByServicePrev3Avg?.freenow) || 0);
                      const ch = getChange(data.totalGross, prev3Avg);
                      const badgeVariant = ch ? (ch.isUp ? "success" : "danger") : "primary";
                      return (
                        <Badge bg={badgeVariant} className="fs-6 px-3 py-2">
                          {formatMoney(data.totalGross)} zł
                        </Badge>
                      );
                    })()}
                  </div>
                  <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                    {(() => {
                      const prev3Avg = (Number(data.incomeByServicePrev3Avg?.uber) || 0) + (Number(data.incomeByServicePrev3Avg?.bolt) || 0) + (Number(data.incomeByServicePrev3Avg?.freenow) || 0);
                      const ch = getChange(data.totalGross, prev3Avg);
                      if (!ch) return null;
                      return (
                        <span className={`d-flex align-items-center gap-1 small ${ch.isUp ? "text-success" : "text-danger"}`}>
                          {ch.isUp ? <FaArrowUp size={14} /> : <FaArrowDown size={14} />}
                          <span>{ch.percent > 0 ? "+" : ""}{ch.percent}%</span>
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Списання та повернення з додаткових коштів — по категоріях */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="py-3 bg-secondary bg-opacity-10">
              <h5 className="mb-0 fw-semibold">
                <AiOutlineArrowDown className="me-2 text-danger" />
                {t("weekStatisticsExtraFunds")}
              </h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                {CATEGORY_KEYS.map(({ key, labelKey, Icon }) => {
                  const ch = getChange(data.totalsByCategory?.[key], data.totalsByCategoryPrev3Avg?.[key]);
                  const isUp = ch?.isUp;
                  const borderClass = ch ? (isUp ? "border-success" : "border-danger") : "border-secondary";
                  return (
                  <Col key={key} xs={6} md={4} lg={3}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setModalCategory(key)}
                      onKeyDown={(e) => e.key === "Enter" && setModalCategory(key)}
                      className={`p-3 rounded border border-2 bg-light bg-opacity-50 ${borderClass}`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Icon className="text-secondary flex-shrink-0" size={22} />
                        <span className="small text-muted text-uppercase fw-semibold">{t(labelKey)}</span>
                      </div>
                      <div className="h5 mb-0 fw-bold text-center">
                        {formatMoney(data.totalsByCategory?.[key] ?? 0)} zł
                      </div>
                      {ch && (
                          <div className={`d-flex align-items-center justify-content-center gap-1 small mt-1 ${isUp ? "text-success" : "text-danger"}`}>
                            {ch.isUp ? <FaArrowUp size={12} /> : <FaArrowDown size={12} />}
                            <span>{ch.percent > 0 ? "+" : ""}{ch.percent}%</span>
                          </div>
                        )}
                    </div>
                  </Col>
                  );
                })}
              </Row>
              <Row className="mt-3 g-2">
                <Col xs={12} md={6}>
                  <div className="p-3 rounded bg-danger bg-opacity-10 border border-danger border-opacity-25 d-flex justify-content-between align-items-center">
                    <span className="fw-semibold text-muted">{t("weekStatisticsTotalDeductions")}</span>
                    <span className="h5 mb-0 fw-bold text-danger">{formatMoney(data.totalsExtra?.totalDeductions ?? 0)} zł</span>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="p-3 rounded bg-success bg-opacity-10 border border-success border-opacity-25 d-flex justify-content-between align-items-center">
                    <span className="fw-semibold text-muted">{t("weekStatisticsTotalRefunds")}</span>
                    <span className="h5 mb-0 fw-bold text-success">{formatMoney(data.totalsExtra?.totalRefunds ?? 0)} zł</span>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Модальне вікно: користувач – сума (та опис для Інші витрати / Повернення коштів) */}
          <Modal show={!!modalCategory} onHide={() => setModalCategory(null)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {modalCategory && t(CATEGORY_KEYS.find((c) => c.key === modalCategory)?.labelKey || modalCategory)}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
              {modalCategory && data?.extraFunds && (() => {
                const cat = CATEGORY_KEYS.find((c) => c.key === modalCategory);
                if (!cat) return null;
                if (cat.hasDetails && cat.detailsField) {
                  const field = cat.detailsField;
                  return (
                    <div className="p-3">
                      {data.extraFunds
                        .filter((ef) => {
                          const total = field === "other_expenses" ? ef.otherExpensesSum : ef.refunds;
                          return (Number(total) || 0) !== 0;
                        })
                        .map((ef) => {
                          const list = Array.isArray(ef[field]) ? ef[field] : [];
                          const total = field === "other_expenses" ? ef.otherExpensesSum : ef.refunds;
                          const name = `${ef.firstName || ""} ${ef.lastName || ""}`.trim();
                          const detailRows = list.filter((x) => (Number(x?.amount) || 0) !== 0 || (x?.description || "").toString().trim());
                          return (
                            <div key={ef.id} className="mb-4">
                              <div className="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2">
                                <strong>{name}</strong>
                                <span className="fw-bold">{formatMoney(total)} zł</span>
                              </div>
                              <Table size="sm" className="mb-0">
                                <thead>
                                  <tr>
                                    <th>{t("description")}</th>
                                    <th className="text-end">{t("amount")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detailRows.length === 0 ? (
                                    <tr>
                                      <td>—</td>
                                      <td className="text-end">{formatMoney(total)} zł</td>
                                    </tr>
                                  ) : (
                                    detailRows.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{(item?.description ?? "").toString().trim() || "—"}</td>
                                        <td className="text-end">{formatMoney(item?.amount ?? 0)} zł</td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          );
                        })}
                      {data.extraFunds.filter((ef) => {
                        const total = field === "other_expenses" ? ef.otherExpensesSum : ef.refunds;
                        return (Number(total) || 0) !== 0;
                      }).length === 0 && (
                        <p className="text-muted mb-0">{t("noRecords")}</p>
                      )}
                    </div>
                  );
                }
                const rows = data.extraFunds.filter((ef) => (Number(ef[cat.key]) || 0) !== 0);
                return (
                  <div className="p-0">
                    {rows.length === 0 ? (
                      <p className="p-3 text-muted mb-0">{t("noRecords")}</p>
                    ) : (
                      <Table responsive hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("driver")}</th>
                            <th className="text-end">{t("amount")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((ef) => (
                            <tr key={ef.id}>
                              <td>{`${ef.firstName || ""} ${ef.lastName || ""}`.trim()}</td>
                              <td className="text-end fw-semibold">{formatMoney(ef[cat.key] ?? 0)} zł</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                );
              })()}
            </Modal.Body>
          </Modal>
        </>
      )}

      {!loading && !data && selectedWeek && !error && (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center py-5 text-muted">
            {t("noRecords")}
          </Card.Body>
        </Card>
      )}

      {!selectedWeek && !loading && (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center py-5 text-muted">
            {t("weekStatisticsSelectWeek")}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default WeekStatistics;
